/**
 * useCannoscaleResolver — pure annotation-scale context resolver (PR-3).
 *
 * AutoCAD's annotative entities (TEXT, MTEXT, ATTRIB, INSERT, HATCH,
 * DIMENSION, MULTILEADER, LEADER, TOLERANCE) carry a list of per-scale
 * `AnnotScaleContext` snapshots. When the user switches CANNOSCALE the
 * renderer must:
 *
 *   1. find the context whose `scaleName` matches the active cannoscale,
 *   2. apply that context's insertion / rotation / per-entity dimensions,
 *   3. or, if no matching context exists, decide visibility based on
 *      ANNOALLVISIBLE (true → still draw using master fields; false → hide).
 *
 * Why "use*" prefix when there is no React state. The plan
 * (`§4.3 Flow B`) names this resolver `useCannoscaleResolver` to follow
 * the existing `useVisibleEntities` convention. It is intentionally a
 * pure function — the caller is the renderer's per-entity walk and we
 * cannot afford a `useMemo` wrapper per entity at 60fps. Tests treat it
 * as a regular function and the hook lint rules are satisfied because
 * we never call any other `use*` here.
 *
 * Why we accept `annotativeContexts` as a typed array (not the raw
 * EntityPreview JSON property). The renderer's call site (renderText /
 * renderMText / renderHatch / renderBlockInstance) parses the JSON
 * property exactly once at the top of the component and passes the
 * decoded array in. This keeps `JSON.parse` out of the hot loop —
 * АБИ1.dxf has ~143 annotative entities, parsing each per render at
 * 60fps would burn ~5ms/frame.
 *
 * Resolution table (by `kind`):
 *
 *   text / mtext / mtext_attribute /
 *   attribute / tolerance         → effectiveScale=ctx.scaleFactor,
 *                                   insertion + rotation override
 *   blockref                      → adds scaleX / scaleY override
 *   hatch                         → patternScale + patternAngle
 *   dimension                     → scaleFactor + textPosition (insertion)
 *   mleader                       → scaleFactor only (legacy MLEADER
 *                                   resolver still owns full dispatch)
 *   leader                        → scaleFactor only
 *
 * The 10-variant exhaustiveness check is enforced by the
 * `_exhaustive: never` assignment in the default branch. Adding a new
 * `kind` to the union without updating the switch will fail typecheck.
 */

import type { AnnotScaleContext } from "@dxf-ai-editor/drawing-model";

/**
 * Output of the resolver — every field is optional (or carries a sane
 * default) so the renderer can spread the resolved fields over the
 * entity's master values:
 *
 *   const ctx = resolveCannoscaleContext(entity, scaleName, annoAllVisible);
 *   if (!ctx.isVisible) return null;
 *   const x = ctx.insertion?.x ?? entity.insertionPoint.x;
 *   const fontSize = baseHeight * ctx.effectiveScale;
 */
export type ResolvedAnnotation = {
  /** Whether the renderer should emit the entity at all. */
  isVisible: boolean;
  /**
   * Multiplier applied to dimensions / heights at the call site. For
   * non-annotative entities this is 1. For annotative entities with a
   * matched context, it is the context's `scaleFactor` (which equals
   * paperUnits/drawingUnits, e.g. 0.01 for 1:100). For annotative
   * entities without a matching context it is 1 — the renderer falls
   * back to the entity's master fields.
   */
  effectiveScale: number;
  /** Per-context insertion override (TEXT, MTEXT, blockref, dimension). */
  insertion?: { x: number; y: number };
  /** Per-context rotation override (TEXT, MTEXT, blockref, hatch). */
  rotation?: number;
  /** Per-context X scale (blockref only). */
  scaleX?: number;
  /** Per-context Y scale (blockref only). */
  scaleY?: number;
  /**
   * The raw active context. `null` when the entity is non-annotative
   * or when it is annotative but no context matched the current scale
   * (and ANNOALLVISIBLE allowed it through anyway).
   */
  activeContext: AnnotScaleContext | null;
};

const NON_ANNOTATIVE: ResolvedAnnotation = {
  isVisible: true,
  effectiveScale: 1,
  activeContext: null,
};

const HIDDEN: ResolvedAnnotation = {
  isVisible: false,
  effectiveScale: 1,
  activeContext: null,
};

const FALLBACK_VISIBLE: ResolvedAnnotation = {
  isVisible: true,
  effectiveScale: 1,
  activeContext: null,
};

/**
 * Resolve the per-scale context for an entity and the active cannoscale.
 *
 * @param contexts - The entity's `annotativeContexts` array (may be empty).
 * @param scaleName - The active CANNOSCALE name (e.g. "1:50"). May be `null`
 *                    when the document has not yet bootstrapped a scale.
 * @param annoAllVisible - The header's ANNOALLVISIBLE flag. When `true`,
 *                    annotative entities without a matching context still
 *                    render using their master fields. When `false`, they
 *                    are hidden — matches AutoCAD's "Show annotations at
 *                    all scales" toggle.
 */
export function resolveCannoscaleContext(
  contexts: ReadonlyArray<AnnotScaleContext> | undefined,
  scaleName: string | null | undefined,
  annoAllVisible: boolean,
): ResolvedAnnotation {
  return resolveImpl(contexts, scaleName, annoAllVisible);
}

/**
 * Plan-aliased name (`useCannoscaleResolver`). Identical semantics to
 * `resolveCannoscaleContext`; the prefix matches the convention in the
 * annotation-scale parity plan §4.3 Flow B and pairs with
 * `useVisibleEntities`.
 */
export function useCannoscaleResolver(
  contexts: ReadonlyArray<AnnotScaleContext> | undefined,
  scaleName: string | null | undefined,
  annoAllVisible: boolean,
): ResolvedAnnotation {
  return resolveImpl(contexts, scaleName, annoAllVisible);
}

function resolveImpl(
  contexts: ReadonlyArray<AnnotScaleContext> | undefined,
  scaleName: string | null | undefined,
  annoAllVisible: boolean,
): ResolvedAnnotation {
  // 1. Non-annotative entity: always visible, no overrides.
  if (!contexts || contexts.length === 0) {
    return NON_ANNOTATIVE;
  }

  // 2. Find the context matching the current scale (when one is set).
  const ctx = scaleName
    ? contexts.find((c) => c.scaleName === scaleName)
    : undefined;

  // 3. No match — visibility falls to ANNOALLVISIBLE; renderer reads
  //    master fields by default. We surface a stable value from the
  //    constants above so equality checks in tests are cheap.
  if (!ctx) {
    return annoAllVisible ? FALLBACK_VISIBLE : HIDDEN;
  }

  // 4. Match found — discriminated dispatch on kind. Returns
  //    `effectiveScale + insertion + rotation + (scaleX/scaleY)` shaped
  //    output the renderer can consume directly.
  return resolveByContextKind(ctx);
}

/**
 * Convert AcDbScale's stored scale factor (paperUnits/drawingUnits, e.g.
 * 0.02 for "1:50") into the render-multiplier convention the renderer
 * expects (`displayed_size = base_size * effectiveScale`).
 *
 * For annotative TEXT/MTEXT/INSERT the master `height` / `scale*` fields
 * carry the **paper-space** measurement; to draw them in model space we
 * need to multiply by `drawingUnits/paperUnits` (the inverse of
 * AcDbScale.scaleFactor). For "1:50" that's 50, so a 2.5mm paper-space
 * TEXT renders at 125 model units — matching AutoCAD parity (see
 * `annoupdateMutation` in command-engine which uses the same
 * `factor = 1/cannoscale` rule for INSERT scaleX/Y).
 *
 * Returns 1 (no-op multiplier) when the context's scaleFactor is
 * zero / non-finite — defensive fallback so a corrupt AcDbScale does
 * not collapse the rendered geometry to the origin.
 */
function asRenderMultiplier(scaleFactor: number): number {
  if (!Number.isFinite(scaleFactor) || scaleFactor === 0) return 1;
  return 1 / scaleFactor;
}

function resolveByContextKind(ctx: AnnotScaleContext): ResolvedAnnotation {
  switch (ctx.kind) {
    case "text":
    case "mtext_attribute":
    case "attribute":
    case "tolerance":
      return {
        isVisible: true,
        effectiveScale: asRenderMultiplier(ctx.scaleFactor),
        insertion: { x: ctx.insertion.x, y: ctx.insertion.y },
        rotation: ctx.rotation,
        activeContext: ctx,
      };

    case "mtext":
      // MTEXT also carries optional `width` / `attachment` per scale —
      // the renderer doesn't use them yet (PR-7 territory) but the
      // common shape (insertion + rotation) is identical to TEXT.
      return {
        isVisible: true,
        effectiveScale: asRenderMultiplier(ctx.scaleFactor),
        insertion: { x: ctx.insertion.x, y: ctx.insertion.y },
        rotation: ctx.rotation,
        activeContext: ctx,
      };

    case "blockref":
      // INSERT carries explicit per-scale `scaleX/scaleY` overrides —
      // the renderer prefers those over the asRenderMultiplier-derived
      // value because AutoCAD writes the already-scaled values into
      // the context-data record (master `Scale X` is paper-space; the
      // context's `scaleX/scaleY` is what the user actually drew).
      return {
        isVisible: true,
        effectiveScale: asRenderMultiplier(ctx.scaleFactor),
        insertion: { x: ctx.insertion.x, y: ctx.insertion.y },
        rotation: ctx.rotation,
        scaleX: ctx.scaleX,
        scaleY: ctx.scaleY,
        activeContext: ctx,
      };

    case "hatch":
      // HATCH boundary stays in world coordinates; only the pattern
      // changes per cannoscale. The renderer applies `effectiveScale`
      // to the pattern fill (PR-3 keeps the boundary as-is). Note:
      // `patternScale` here is the absolute per-scale pattern scale
      // (already accounting for cannoscale) — surfacing it directly
      // avoids the asRenderMultiplier adjustment.
      return {
        isVisible: true,
        effectiveScale: ctx.patternScale,
        rotation: ctx.patternAngle,
        activeContext: ctx,
      };

    case "dimension":
      // DIMENSION uses its own legacy resolver (renderDimension.tsx)
      // for textHeight / arrowheadSize overrides — this branch only
      // surfaces the per-scale text position so callers that still
      // need a generic answer get one.
      return {
        isVisible: true,
        effectiveScale: asRenderMultiplier(ctx.scaleFactor),
        ...(ctx.textPosition
          ? {
              insertion: {
                x: ctx.textPosition.x,
                y: ctx.textPosition.y,
              },
            }
          : {}),
        activeContext: ctx,
      };

    case "mleader":
      return {
        isVisible: true,
        effectiveScale: asRenderMultiplier(ctx.scaleFactor),
        activeContext: ctx,
      };

    case "leader":
      return {
        isVisible: true,
        effectiveScale: asRenderMultiplier(ctx.scaleFactor),
        activeContext: ctx,
      };

    default: {
      // Exhaustiveness sentinel — adding a new context kind without
      // covering it here is a typecheck error.
      const _exhaustive: never = ctx;
      void _exhaustive;
      return NON_ANNOTATIVE;
    }
  }
}

/**
 * Helper for renderer call sites that hold the contexts as a JSON
 * string (the `__entity_annotative_contexts` property bag entry).
 *
 * Returns `[]` on parse error so the resolver behaves like a
 * non-annotative entity — never throws inside a render loop.
 */
export function decodeAnnotativeContextsJson(
  raw: string | undefined,
): AnnotScaleContext[] {
  if (!raw || raw.length === 0) return [];
  try {
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed as AnnotScaleContext[];
  } catch {
    return [];
  }
}
